/**
 * Extremely lightweight mock for the `zeromq` package so browser playgrounds can
 * import server code without pulling native bindings.
 */

import { EventEmitter } from './events.js';

export type ZmqMessage = any;
export type ZmqAddress = string;

class MockSocket extends EventEmitter {
  linger: number;
  protected boundAddresses: Set<ZmqAddress> = new Set();
  protected connectedAddresses: Set<ZmqAddress> = new Set();
  protected subscriptions: Set<string> = new Set();

  constructor(options: { linger?: number } = {}) {
    super();
    this.linger = options.linger ?? 0;
  }

  async bind(address: ZmqAddress): Promise<void> {
    this.boundAddresses.add(address);
  }

  async unbind(address: ZmqAddress): Promise<void> {
    this.boundAddresses.delete(address);
  }

  connect(address: ZmqAddress): void {
    this.connectedAddresses.add(address);
  }

  disconnect(address: ZmqAddress): void {
    this.connectedAddresses.delete(address);
  }

  subscribe(topic: string): void {
    this.subscriptions.add(topic);
  }

  unsubscribe(topic: string): void {
    this.subscriptions.delete(topic);
  }

  send(_frames: any[]): void {
    // no-op
  }

  async close(): Promise<void> {
    this.boundAddresses.clear();
    this.connectedAddresses.clear();
    this.subscriptions.clear();
  }

  async *[Symbol.asyncIterator](): AsyncIterableIterator<[string, ZmqMessage]> {
    return;
  }
}

export class Publisher extends MockSocket {}

export class Subscriber extends MockSocket {
  async *[Symbol.asyncIterator](): AsyncIterableIterator<[string, ZmqMessage]> {
    // Immediately end the iterator so `for await` loops exit gracefully.
    return;
  }
}

export const socket = (): MockSocket => new MockSocket();

// The ZAP handler socket and the context it is keyed by (packages/server/src/trust/zap.ts).
// No handshake ever happens on this transport, so neither does anything.
export class Reply extends MockSocket {}
export const context = {};

// Every server owns a CurveZMQ identity (packages/server/src/trust), generated at boot
// when none is configured. Browser playgrounds run on this no-op transport, so the keys
// secure nothing here — but they must be well-formed Z85 (40 chars → 32 bytes) because
// the server validates them. Math.random keeps installDeterministicGlobals() seeding.
const Z85_ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ.-:+=^!/*?&<>()[]{}@%$#';
const randomZ85Key = (): string => {
  let out = '';
  for (let block = 0; block < 8; block++) {
    let value = 0;
    for (let i = 0; i < 4; i++) value = value * 256 + Math.floor(Math.random() * 256);
    let chunk = '';
    for (let i = 0; i < 5; i++) {
      chunk = Z85_ALPHABET[value % 85] + chunk;
      value = Math.floor(value / 85);
    }
    out += chunk;
  }
  return out;
};
export const curveKeyPair = (): { publicKey: string; secretKey: string } => ({
  publicKey: randomZ85Key(),
  secretKey: randomZ85Key(),
});

export default {
  Publisher,
  Subscriber,
  socket,
  curveKeyPair,
  Reply,
  context,
};
